import type { RealtimeMessage } from '@atlas/types'
import { EventType, FileJobStatus } from '@atlas/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { RealtimeConnectionState, subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { fileJobApi, type ZipRequestSummary, zipJobListKey } from '@/services/file-job.service'

/** Statuses a recovered row is still working through. */
const ACTIVE_STATUSES: ReadonlySet<FileJobStatus> = new Set([FileJobStatus.QUEUED, FileJobStatus.PROCESSING])

/** Poll cadence while the push channel is down. */
const POLL_MS = 5_000

/** Reduced cadence while the push channel is healthy — a dropped message must not strand a row on "Preparing…" forever. */
const CONNECTED_POLL_MS = 30_000

/** The `requestId` a terminal message names, read defensively. The payload decides nothing beyond "ask again now". */
function requestIdOf(message: RealtimeMessage): string | null {
	const data = message.data as { requestId?: unknown } | undefined
	return typeof data?.requestId === 'string' && data.requestId.length > 0 ? data.requestId : null
}

/**
 * Files-local recovery of currently useful ZIP work — what Recent downloads
 * renders, reloaded from the backend every time Library mounts.
 *
 * Deliberately separate from the session monitor: this hook never registers a
 * request with it and never raises a notification, so a row recovered here
 * cannot produce a retroactive global announcement for work the user did not
 * start in this session. Its only outputs are the rows it returns and the
 * listing refetches it asks for.
 *
 * A `FAILED` row is only shown when this mount actually WATCHED it fail. A
 * failure the backend has known about since before Library was opened is not a
 * completion the user is waiting on, and resurrecting it after a refresh would
 * be telling them about something they already dealt with — so the failed set is
 * built from what the LISTING reports across two reads, never from a realtime
 * payload, which is a hint and not a state.
 */
export function useFileJobRecovery() {
	const queryClient = useQueryClient()
	const isPushing = useRealtimeConnectionState() === RealtimeConnectionState.CONNECTED

	const query = useQuery({
		queryKey: zipJobListKey(),
		queryFn: () => fileJobApi.listZipRequests(),
		refetchInterval: (q) => {
			const anyActive = (q.state.data?.items ?? []).some((row) => ACTIVE_STATUSES.has(row.status))
			if (!anyActive) return false
			return isPushing ? CONNECTED_POLL_MS : POLL_MS
		},
	})

	const items = query.data?.items
	// Ids this mount has seen the LISTING report as still working. A row that was
	// never in here cannot enter the failed set below, which is what keeps a
	// pre-existing backend failure off the surface.
	const seenActive = useRef(new Set<string>())
	const [locallyFailed, setLocallyFailed] = useState<ReadonlySet<string>>(new Set())

	useEffect(() => {
		if (!items) return
		const newlyFailed: string[] = []
		for (const row of items) {
			if (ACTIVE_STATUSES.has(row.status)) seenActive.current.add(row.requestId)
			else if (row.status === FileJobStatus.FAILED && seenActive.current.has(row.requestId)) {
				newlyFailed.push(row.requestId)
			}
		}
		if (newlyFailed.length === 0) return
		setLocallyFailed((previous) => {
			if (newlyFailed.every((id) => previous.has(id))) return previous
			return new Set([...previous, ...newlyFailed])
		})
	}, [items])

	// A string, not an array: the query returns a fresh array on every refetch, so
	// an array dependency would re-register the subscription each time.
	const activeIdKey = (items ?? [])
		.filter((row) => ACTIVE_STATUSES.has(row.status))
		.map((row) => row.requestId)
		.join(',')

	useEffect(() => {
		if (!activeIdKey) return
		const activeIds = new Set(activeIdKey.split(','))
		// Unnarrowed by `entityRef.id`, because for a File Job event that id is the
		// SHARED job — several requests can point at one. The requester's own
		// request is only named inside `data`.
		return subscribeRealtime({ type: [EventType.FILE_JOB_READY, EventType.FILE_JOB_FAILED] }, (message) => {
			const requestId = requestIdOf(message)
			if (!requestId || !activeIds.has(requestId)) return
			void queryClient.invalidateQueries({ queryKey: zipJobListKey() })
		})
	}, [activeIdKey, queryClient])

	// `EXPIRED` is dropped defensively as well as by the backend's own listing: a
	// result that is no longer downloadable is not useful work, and a row that
	// expires between two reads must disappear rather than sit there offering a
	// download that cannot succeed.
	const rows: ZipRequestSummary[] = (items ?? []).filter(
		(row) =>
			row.status !== FileJobStatus.EXPIRED && (row.status !== FileJobStatus.FAILED || locallyFailed.has(row.requestId)),
	)

	return { rows, isLoading: query.isLoading }
}
